/**
 * The quote page's Publish and Share (V1 completion E6), and Withdraw (docs/features/order-to-invoice/
 * 40_quotes_module_spec.md §5): no reason is asked, because none is stored.
 * The customer's link then says the quote is withdrawn. (Name versions is
 * shared with the order page: `orders/NameVersionsDialog`.)
 *
 * It posts through the page (`onConfirm`), which answers with the refusal to
 * show in the banner. The modal's own confirm is its one primary button.
 */
import { useId, useState } from "react";
import type { QuoteView } from "@shared-core/data/quote-types";
import {
  contactName,
  type QuoteStepFailure,
} from "@shared-core/domain/quote-presentation";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";

export function WithdrawQuoteDialog({
  quote,
  busy,
  onClose,
  onConfirm,
}: {
  quote: QuoteView;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => Promise<QuoteStepFailure | null>;
}) {
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const isDraft = quote.status === "draft";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isDraft ? "Discard" : "Withdraw"} {quote.quote_no}
          </DialogTitle>
          <DialogDescription>
            {isDraft
              ? "The draft is thrown away: nobody outside staff has seen it. It can't be reopened."
              : `${contactName(quote)}'s link will say the quote is withdrawn, and the reminders stop. A withdrawn quote can't be reopened.`}
          </DialogDescription>
        </DialogHeader>
        {failure && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure.summary}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={async () => {
              const refused = await onConfirm();
              setFailure(refused);
              if (!refused) onClose();
            }}
          >
            {isDraft ? "Discard draft" : "Withdraw quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Publishing a channel's draft with checks still open: allowed (nothing in
 * quotes refuses it), but said, because publishing drops the price-list
 * quantities not chosen and the possible-alternative flags.
 */
function OpenChecksWarning({ openChecks }: { openChecks: string | null }) {
  if (!openChecks) return null;
  return (
    <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
      <b className="font-semibold">Still to check:</b> {openChecks}. Publishing
      drops the price-list quantities you have not chosen, and keeps every line
      as it stands.
    </p>
  );
}

/** An optional expiry (a date) as the end of that day, which is what publishing takes; empty means the default. */
function expiryOf(day: string): string | undefined {
  return day ? new Date(`${day}T23:59:59`).toISOString() : undefined;
}

/**
 * Publish a draft: its prices are fixed and its expiry starts, but nothing goes to the customer until it is
 * shared. The expiry is optional (the default is two weeks on).
 */
export function PublishQuoteDialog({
  quote,
  openChecks = null,
  busy,
  onClose,
  onConfirm,
}: {
  quote: QuoteView;
  /** A channel's draft: what its owner has not decided yet ("2 lines with price-list quantities to choose from"). */
  openChecks?: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (
    expiresAt: string | undefined,
  ) => Promise<QuoteStepFailure | null>;
}) {
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const [expires, setExpires] = useState("");
  const id = useId();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publish {quote.quote_no}</DialogTitle>
          <DialogDescription>
            Its prices are fixed from here, and a change is a new quote that
            replaces it. Nothing goes to {contactName(quote)} until you share
            it.
          </DialogDescription>
        </DialogHeader>
        <OpenChecksWarning openChecks={openChecks} />
        <div className="space-y-1">
          <Label htmlFor={`${id}-expires`}>Expires (optional)</Label>
          <Input
            id={`${id}-expires`}
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            aria-invalid={failure?.fieldErrors.expires_at ? true : undefined}
          />
          <p className="text-xs text-muted-foreground">
            Left empty, it expires two weeks after it is published.
          </p>
          {failure?.fieldErrors.expires_at && (
            <p className="text-sm text-red-600">
              {failure.fieldErrors.expires_at}
            </p>
          )}
        </div>
        {failure && !failure.fieldErrors.expires_at && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure.summary}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={async () => {
              const refused = await onConfirm(expiryOf(expires));
              setFailure(refused);
              if (!refused) onClose();
            }}
          >
            Publish quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Share a quote: email the contact their link, as often as asked (each share is recorded and moves the reminders
 * on). A draft is published first, so its expiry may be set here.
 */
export function ShareQuoteDialog({
  quote,
  openChecks = null,
  busy,
  onClose,
  onConfirm,
}: {
  quote: QuoteView;
  /** A channel's draft: what its owner has not decided yet ("2 lines with price-list quantities to choose from"). */
  openChecks?: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (
    expiresAt: string | undefined,
  ) => Promise<QuoteStepFailure | null>;
}) {
  const [failure, setFailure] = useState<QuoteStepFailure | null>(null);
  const [expires, setExpires] = useState("");
  const id = useId();
  const isDraft = quote.status === "draft";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share {quote.quote_no}</DialogTitle>
          <DialogDescription>
            Emails {contactName(quote)}
            {quote.contact && ` (${quote.contact.email})`} a link to the quote.
            {isDraft
              ? " This also publishes the draft: its prices are fixed from here."
              : " Each share is recorded, and the reminders count from the latest."}
          </DialogDescription>
        </DialogHeader>
        <OpenChecksWarning openChecks={openChecks} />
        {isDraft && (
          <div className="space-y-1">
            <Label htmlFor={`${id}-expires`}>Expires (optional)</Label>
            <Input
              id={`${id}-expires`}
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              aria-invalid={failure?.fieldErrors.expires_at ? true : undefined}
            />
            {failure?.fieldErrors.expires_at && (
              <p className="text-sm text-red-600">
                {failure.fieldErrors.expires_at}
              </p>
            )}
          </div>
        )}
        {failure && !failure.fieldErrors.expires_at && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{failure.summary}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={async () => {
              const refused = await onConfirm(
                isDraft ? expiryOf(expires) : undefined,
              );
              setFailure(refused);
              if (!refused) onClose();
            }}
          >
            {isDraft ? "Publish and share" : "Share link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
